import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { authClient } from "@/lib/auth-client";
import { userKeys } from "@/lib/query";

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  jobRole: string | null;
  createdAt: Date;
}

export type { User };

export function useUsers(filters: { search?: string }) {
  return useQuery({
    queryKey: userKeys.list(filters),
    queryFn: async (): Promise<User[]> => {
      const { data, error } = await authClient.admin.listUsers({
        query: {
          limit: 100,
          ...(filters.search
            ? {
                searchField: "email" as const,
                searchValue: filters.search,
                searchOperator: "contains" as const,
              }
            : {}),
        },
      });
      if (error) throw error;
      return (data?.users as User[]) ?? [];
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; email: string; password: string; role: string }) => {
      const { error } = await authClient.admin.createUser(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User created");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to create user");
    },
  });
}

export function useChangeRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; role: string }) => {
      const { error } = await authClient.admin.setRole(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("Role updated");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to change role");
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await authClient.admin.removeUser({ userId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to delete user");
    },
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; newPassword: string }) => {
      const { error } = await authClient.admin.setUserPassword(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("Password reset");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to reset password");
    },
  });
}

export function useSetUserJobRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, jobRole }: { userId: string; jobRole: string | null }) => {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobRole }),
      });
      if (!res.ok) throw new Error("Failed to set user job role");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("Job role updated");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to set user job role");
    },
  });
}
